import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { ClickableAvatar } from "../../components/ClickableAvatar";
import { toast } from "sonner";
import {
  Search, Filter, RefreshCw, ChevronLeft, ChevronRight, MessageCircle,
  CheckCircle2, Clock, XCircle, Eye, Phone, User, X
} from "lucide-react";
import { format } from "date-fns";

const STATUS_COLORS = {
  New: "bg-blue-500/15 text-blue-500 border-blue-500/20",
  Sent: "bg-amber-500/15 text-amber-500 border-amber-500/20",
  Delivered: "bg-emerald-500/15 text-emerald-500 border-emerald-500/20",
  Read: "bg-green-500/15 text-green-500 border-green-500/20",
  Failed: "bg-red-500/15 text-red-500 border-red-500/20"
};

const STATUS_ICONS = {
  New: Clock,
  Sent: Clock,
  Delivered: CheckCircle2,
  Read: Eye,
  Failed: XCircle
};

export default function WhatsAppActivity() {
  const { currentBranch } = useAuth();
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState(null);
  
  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: "",
    category: "",
    dateFrom: "",
    dateTo: ""
  });
  
  // Resend loading state
  const [resendLoading, setResendLoading] = useState(null);

  useEffect(() => {
    fetchStats();
  }, [currentBranch]);

  useEffect(() => {
    fetchActivity();
  }, [page, currentBranch]);

  const fetchStats = async () => {
    try {
      const params = new URLSearchParams();
      if (currentBranch?.id) params.append("branchId", currentBranch.id);
      
      const res = await api.get(`/whatsapp/activity/stats?${params}`);
      setStats(res.data);
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    }
  };

  const fetchActivity = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: "20" });
      if (currentBranch?.id) params.append("branchId", currentBranch.id);
      if (filters.status) params.append("status", filters.status);
      if (filters.category) params.append("category", filters.category);
      if (filters.dateFrom) params.append("dateFrom", filters.dateFrom);
      if (filters.dateTo) params.append("dateTo", filters.dateTo);
      
      const res = await api.get(`/whatsapp/activity?${params}`);
      setMessages(res.data.messages || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      console.error("Failed to fetch activity:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchActivity();
  };

  const clearFilters = () => {
    setFilters({
      status: "",
      category: "",
      dateFrom: "",
      dateTo: ""
    });
    setPage(1);
    fetchActivity();
  };

  const handleResend = async (messageId) => {
    setResendLoading(messageId);
    try {
      await api.post("/whatsapp/messages/resend", { messageId });
      toast.success("Message resent successfully");
      fetchActivity();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resend message");
    } finally {
      setResendLoading(null);
    }
  };

  const hasActiveFilters = filters.status || filters.category || filters.dateFrom || filters.dateTo;

  return (
    <div className="space-y-6" data-testid="whatsapp-activity-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-black font-heading tracking-tight uppercase">
            Message Activity
          </h1>
          <p className="text-sm text-muted-foreground">
            Track all WhatsApp message deliveries and statuses
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={showFilters ? "default" : "outline"}
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="font-heading uppercase tracking-wide text-xs"
            data-testid="toggle-filters-btn"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            {hasActiveFilters && (
              <Badge className="ml-2 h-5 w-5 p-0 flex items-center justify-center bg-primary-foreground text-primary text-[10px]">
                !
              </Badge>
            )}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-black font-heading">{stats.total}</p>
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Total</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-black font-heading text-blue-500">{stats.todayCount}</p>
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Today</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-black font-heading text-emerald-500">{stats.delivered}</p>
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Delivered</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-black font-heading text-green-500">{stats.read}</p>
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Read</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-black font-heading text-red-500">{stats.failed}</p>
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Failed</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="space-y-1">
                <Label className="text-[10px] font-mono uppercase">Status</Label>
                <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
                  <SelectTrigger className="h-9" data-testid="filter-status">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="New">New</SelectItem>
                    <SelectItem value="Sent">Sent</SelectItem>
                    <SelectItem value="Delivered">Delivered</SelectItem>
                    <SelectItem value="Read">Read</SelectItem>
                    <SelectItem value="Failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] font-mono uppercase">Category</Label>
                <Select value={filters.category || "all"} onValueChange={(v) => setFilters({ ...filters, category: v === "all" ? "" : v })}>
                  <SelectTrigger className="h-9" data-testid="filter-category">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="Renewal">Renewal</SelectItem>
                    <SelectItem value="Expiry">Expiry</SelectItem>
                    <SelectItem value="Attendance">Attendance</SelectItem>
                    <SelectItem value="Lead">Lead</SelectItem>
                    <SelectItem value="Workouts">Workouts</SelectItem>
                    <SelectItem value="Progress">Progress</SelectItem>
                    <SelectItem value="Goal">Goal</SelectItem>
                    <SelectItem value="General">General</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] font-mono uppercase">Date From</Label>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                  className="h-9"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] font-mono uppercase">Date To</Label>
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                  className="h-9"
                />
              </div>
            </div>
            {hasActiveFilters && (
              <div className="mt-4 pt-4 border-t border-border flex justify-between items-center">
                <Button variant="outline" size="sm" onClick={handleSearch} className="text-xs">
                  Apply Filters
                </Button>
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs">
                  <X className="h-3.5 w-3.5 mr-1" />
                  Clear Filters
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Activity Table */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>No messages found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-secondary/30">
                    <th className="text-left px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                      Member
                    </th>
                    <th className="text-left px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                      Date & Time
                    </th>
                    <th className="text-left px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                      Template
                    </th>
                    <th className="text-left px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                      Delivered
                    </th>
                    <th className="text-left px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                      Read
                    </th>
                    <th className="text-left px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                      Status
                    </th>
                    <th className="text-left px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {messages.map((msg) => {
                    const StatusIcon = STATUS_ICONS[msg.status] || Clock;
                    return (
                      <tr key={msg.id} className="border-b border-border last:border-0 hover:bg-secondary/20 transition-colors">
                        {/* Member */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <ClickableAvatar
                              src={msg.member?.photo}
                              name={msg.member?.name}
                              className="h-9 w-9 border border-border"
                              fallbackClassName="bg-secondary text-xs"
                            />
                            <div>
                              <p className="font-medium text-sm">{msg.member?.name || "Unknown"}</p>
                              <p className="text-xs text-muted-foreground flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {msg.phone}
                              </p>
                            </div>
                          </div>
                        </td>
                        
                        {/* Date & Time */}
                        <td className="px-4 py-3">
                          {msg.sentAt ? (
                            <div>
                              <p className="text-sm">{format(new Date(msg.sentAt), "MMM d, yyyy")}</p>
                              <p className="text-xs text-muted-foreground">{format(new Date(msg.sentAt), "HH:mm")}</p>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        
                        {/* Template */}
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-sm font-medium">{msg.template?.name || "N/A"}</p>
                            <div className="flex items-center gap-1 mt-1">
                              <Badge variant="outline" className="text-[10px]">{msg.category}</Badge>
                              {msg.template?.subCategory && (
                                <Badge variant="secondary" className="text-[10px]">{msg.template.subCategory}</Badge>
                              )}
                            </div>
                          </div>
                        </td>
                        
                        {/* Delivered */}
                        <td className="px-4 py-3">
                          {msg.deliveredAt ? (
                            <div>
                              <p className="text-sm">{format(new Date(msg.deliveredAt), "MMM d")}</p>
                              <p className="text-xs text-muted-foreground">{format(new Date(msg.deliveredAt), "HH:mm")}</p>
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </td>
                        
                        {/* Read */}
                        <td className="px-4 py-3">
                          {msg.readAt ? (
                            <div>
                              <p className="text-sm">{format(new Date(msg.readAt), "MMM d")}</p>
                              <p className="text-xs text-muted-foreground">{format(new Date(msg.readAt), "HH:mm")}</p>
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </td>
                        
                        {/* Status */}
                        <td className="px-4 py-3">
                          <Badge className={`${STATUS_COLORS[msg.status]} border text-[10px] font-mono uppercase`}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {msg.status}
                          </Badge>
                        </td>
                        
                        {/* Action */}
                        <td className="px-4 py-3">
                          {msg.status === "Failed" && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleResend(msg.id)}
                              disabled={resendLoading === msg.id}
                              className="text-xs"
                              data-testid={`resend-btn-${msg.id}`}
                            >
                              {resendLoading === msg.id ? (
                                <RefreshCw className="h-3.5 w-3.5 mr-1 animate-spin" />
                              ) : (
                                <RefreshCw className="h-3.5 w-3.5 mr-1" />
                              )}
                              Resend
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {page} of {totalPages} ({total} total)
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
